import { useTranslation } from 'react-i18next';
import { Check, X, SkipForward, User, ZoomIn } from 'lucide-react';
import { useState } from 'react';
import type { FaceMergeDecision, MergeDecisionType } from '@/api/types/face.types';

interface FaceComparisonViewProps {
  pair: FaceMergeDecision;
  onDecide: (decision: MergeDecisionType) => void;
  isSubmitting?: boolean;
}

function FaceCard({
  cropUrl,
  photoUrl: _photoUrl,
  confidence,
  label,
}: {
  cropUrl: string | null;
  photoUrl: string | null;
  confidence: number;
  label: string;
}) {
  const [showZoomed, setShowZoomed] = useState(false);

  return (
    <div className="flex flex-col items-center">
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <div className="group relative">
        {cropUrl ? (
          <img
            src={cropUrl}
            alt={label}
            className="h-40 w-40 rounded-xl object-cover shadow-lg ring-1 ring-gray-200 transition-transform group-hover:scale-105 dark:ring-gray-700 sm:h-48 sm:w-48"
          />
        ) : (
          <div className="flex h-40 w-40 items-center justify-center rounded-xl bg-gray-100 shadow-lg dark:bg-gray-700 sm:h-48 sm:w-48">
            <User className="h-16 w-16 text-gray-400" />
          </div>
        )}
        {cropUrl && (
          <button
            onClick={() => setShowZoomed(true)}
            className="absolute bottom-2 end-2 rounded-full bg-black/50 p-1.5 opacity-0 transition-opacity group-hover:opacity-100"
          >
            <ZoomIn className="h-4 w-4 text-white" />
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {(confidence * 100).toFixed(0)}% confidence
      </p>

      {/* Zoomed modal */}
      {showZoomed && cropUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowZoomed(false)}
        >
          <img
            src={cropUrl}
            alt={label}
            className="max-h-[80vh] max-w-[80vw] rounded-xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

export function FaceComparisonView({
  pair,
  onDecide,
  isSubmitting = false,
}: FaceComparisonViewProps) {
  const { t } = useTranslation('faces');
  const similarity = ((1 - pair.distance) * 100).toFixed(0);

  return (
    <div className="flex flex-col items-center">
      {/* Similarity score */}
      <div className="mb-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('similarityScore')}
        </p>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {similarity}%
        </p>
      </div>

      {/* Side-by-side faces */}
      <div className="mb-8 flex items-center gap-6 sm:gap-10">
        <FaceCard
          cropUrl={pair.face_a.crop_url}
          photoUrl={pair.face_a.photo_thumbnail_url}
          confidence={pair.face_a.confidence}
          label={t('faceA')}
        />

        <div className="flex flex-col items-center gap-1 text-gray-400">
          <span className="text-lg font-light">vs</span>
        </div>

        <FaceCard
          cropUrl={pair.face_b.crop_url}
          photoUrl={pair.face_b.photo_thumbnail_url}
          confidence={pair.face_b.confidence}
          label={t('faceB')}
        />
      </div>

      {/* Question */}
      <p className="mb-4 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('samePersonQuestion')}
      </p>

      {/* Decision buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => onDecide('same')}
          disabled={isSubmitting}
          className="flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
          {t('yesSamePerson')}
        </button>
        <button
          onClick={() => onDecide('different')}
          disabled={isSubmitting}
          className="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
          {t('noDifferent')}
        </button>
        <button
          onClick={() => onDecide('skip')}
          disabled={isSubmitting}
          className="flex items-center gap-2 rounded-lg bg-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          <SkipForward className="h-4 w-4" />
          {t('unsure')}
        </button>
      </div>
    </div>
  );
}
